"use client";

import * as React from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  AGENT_PROFILE_PRESETS,
  parseAgentPresetLabel,
  presetFromUrl,
} from "~/lib/onboarding/agent-profile-presets";
import { memberDisplayName, splitDisplayName } from "~/lib/marketplace/hire-team-roster";

export type HireTeamMemberFormValues = {
  displayName?: string;
  firstName: string;
  lastName: string;
  email: string;
  title?: string;
  phone?: string;
  imageUrl?: string;
};

type HireTeamMemberDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  initialValues: HireTeamMemberFormValues;
  onSave: (values: HireTeamMemberFormValues) => void;
  submitLabel?: string;
};

const DEFAULT_TEAM_IMAGE =
  "https://qzmrh6hprwvcouzh.public.blob.vercel-storage.com/icon-55H0ZQhxxToq0QGzxl0GwE1lgNeElP.jpeg";

function normalizeInitialValues(values: HireTeamMemberFormValues): HireTeamMemberFormValues {
  const displayName =
    values.displayName?.trim() ||
    memberDisplayName(values) ||
    "";
  const { firstName, lastName } = displayName
    ? splitDisplayName(displayName)
    : { firstName: values.firstName, lastName: values.lastName };

  return {
    ...values,
    displayName,
    firstName: firstName || values.firstName,
    lastName: lastName || values.lastName,
    imageUrl: values.imageUrl?.trim() || DEFAULT_TEAM_IMAGE,
  };
}

export function HireTeamMemberDialog({
  open,
  onOpenChange,
  title,
  description,
  initialValues,
  onSave,
  submitLabel = "Save",
}: HireTeamMemberDialogProps) {
  const [values, setValues] = React.useState(() => normalizeInitialValues(initialValues));
  const [isUploading, setIsUploading] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setValues(normalizeInitialValues(initialValues));
    }
  }, [open]);

  const displayName = values.displayName ?? memberDisplayName(values);
  const imageUrl = values.imageUrl?.trim() || DEFAULT_TEAM_IMAGE;
  const selectedPreset =
    presetFromUrl(imageUrl) ?? AGENT_PROFILE_PRESETS[0]!;

  const handleImageUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const response = await fetch(`/api/upload/avatar?filename=${encodeURIComponent(file.name)}`, {
        method: "POST",
        body: file,
      });
      if (!response.ok) throw new Error("Upload failed");
      const result = (await response.json()) as { url?: string };
      if (!result.url) throw new Error("No URL returned");
      setValues((prev) => ({ ...prev, imageUrl: result.url }));
      toast.success("Profile image uploaded");
    } catch {
      toast.error("Failed to upload profile image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = () => {
    const trimmedDisplay = displayName.trim();
    const { firstName, lastName } = splitDisplayName(trimmedDisplay);
    if (!trimmedDisplay || !firstName) {
      toast.error("Enter a display name.");
      return;
    }
    if (!values.email.trim()) {
      toast.error("Enter an email address.");
      return;
    }

    onSave({
      ...values,
      displayName: trimmedDisplay,
      firstName,
      lastName,
      email: values.email.trim(),
      title: values.title?.trim() || undefined,
      phone: values.phone?.trim() || undefined,
      imageUrl: values.imageUrl?.trim() || DEFAULT_TEAM_IMAGE,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : null}
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <Avatar className="h-16 w-16 border border-slate-200">
                <AvatarImage src={imageUrl} alt={displayName || "Team member"} className="object-cover" />
                <AvatarFallback>
                  {(displayName || "TM").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <label
                className="absolute -bottom-1 -right-1 cursor-pointer rounded-full border border-white bg-primary p-1.5 text-white shadow hover:bg-primary/90"
                aria-label="Upload profile image"
              >
                {isUploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Camera className="h-3.5 w-3.5" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  disabled={isUploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleImageUpload(file);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>

            <div className="min-w-0 flex-1 space-y-2">
              <Label>Profile image</Label>
              <Select
                value={imageUrl}
                onValueChange={(url) => setValues((prev) => ({ ...prev, imageUrl: url }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose default image">
                    <div className="flex items-center gap-2">
                      <Camera className="h-4 w-4 text-muted-foreground" aria-hidden />
                      <span>{parseAgentPresetLabel(selectedPreset.label).agentName}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {AGENT_PROFILE_PRESETS.map((preset) => (
                    <SelectItem key={preset.url} value={preset.url}>
                      <div className="flex items-center gap-2">
                        <img src={preset.url} alt="" className="h-6 w-6 rounded-full object-cover" />
                        <span>{parseAgentPresetLabel(preset.label).agentName}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Upload a photo or choose a default avatar.</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hire-team-display-name">Display Name *</Label>
            <Input
              id="hire-team-display-name"
              placeholder="e.g., John Doe"
              value={displayName}
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  displayName: e.target.value,
                }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hire-team-email">Email *</Label>
            <Input
              id="hire-team-email"
              type="email"
              placeholder="name@example.com"
              value={values.email}
              onChange={(e) => setValues((prev) => ({ ...prev, email: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hire-team-title">Job Title</Label>
            <Input
              id="hire-team-title"
              placeholder="e.g., Office Manager"
              value={values.title ?? ""}
              onChange={(e) => setValues((prev) => ({ ...prev, title: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hire-team-phone">Phone</Label>
            <Input
              id="hire-team-phone"
              placeholder="(555) 123-4567"
              value={values.phone ?? ""}
              onChange={(e) => setValues((prev) => ({ ...prev, phone: e.target.value }))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!displayName.trim() || !values.email.trim() || isUploading}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
